import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { FiLogOut, FiEdit2, FiSave, FiX } from 'react-icons/fi';

const ProfileContainer = styled.div`
  min-height: calc(100vh - 200px);
  padding: 60px 20px;
  background: var(--background-dark);
`;

const ProfileContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 40px;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 20px;
  }
`;

const Title = styled.h1`
  color: var(--text-primary);
  font-size: 32px;
  font-weight: 700;
`;

const LogoutButton = styled.button`
  padding: 12px 24px;
  background: linear-gradient(135deg, var(--primary-color), var(--secondary-color));
  border: none;
  border-radius: 10px;
  color: #FFFFFF;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(212, 175, 55, 0.3);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(212, 175, 55, 0.4);
  }
`;

const GridContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 30px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled(motion.div)`
  background: var(--background-lighter);
  border: 1px solid var(--border-color);
  border-radius: 20px;
  padding: 35px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, var(--primary-color), var(--accent-color));
  }
  
  &:hover {
    box-shadow: 0 12px 40px rgba(212, 175, 55, 0.2);
    border-color: var(--accent-color);
    transform: translateY(-4px);
  }
`;

const CardTitle = styled.h2`
  color: var(--text-primary);
  font-size: 22px;
  margin-bottom: 25px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-weight: 600;
`;

const EditButton = styled.button`
  background: none;
  border: none;
  color: var(--primary-color);
  cursor: pointer;
  font-size: 18px;
  transition: all 0.3s ease;

  &:hover {
    transform: scale(1.1);
    color: var(--secondary-color);
  }
`;

const AddButton = styled.button`
  padding: 10px 20px;
  background: linear-gradient(135deg, #4caf50, #45a049);
  border: none;
  border-radius: 8px;
  color: #fff;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 15px rgba(76, 175, 80, 0.4);
  }
`;

const InfoGroup = styled.div`
  margin-bottom: 16px;
`;

const Label = styled.label`
  color: var(--text-secondary);
  font-size: 13px;
  text-transform: uppercase;
  letter-spacing: 1px;
  display: block;
  margin-bottom: 8px;
  font-weight: 600;
`;

const Value = styled.p`
  color: var(--text-primary);
  font-size: 16px;
  margin: 0;
  font-weight: 500;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 16px;
  background: #FFFFFF;
  border: 2px solid var(--border-color);
  border-radius: 10px;
  color: var(--text-primary);
  font-size: 15px;
  margin-top: 6px;
  transition: all 0.3s ease;

  &:focus {
    outline: none;
    border-color: var(--primary-color);
    box-shadow: 0 0 0 3px rgba(212, 175, 55, 0.1);
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 20px;
`;

const SaveButton = styled.button`
  flex: 1;
  padding: 12px;
  background: linear-gradient(135deg, #4caf50, #45a049);
  border: none;
  border-radius: 10px;
  color: #fff;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(76, 175, 80, 0.3);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(76, 175, 80, 0.4);
  }
`;

const CancelButton = styled.button`
  flex: 1;
  padding: 12px;
  background: var(--text-tertiary);
  border: 2px solid var(--border-color);
  border-radius: 10px;
  color: var(--text-primary);
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  transition: all 0.3s ease;

  &:hover {
    background: var(--background-darker);
    border-color: var(--text-secondary);
  }
`;

const OrdersList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const OrderItem = styled(motion.div)`
  background: #FFFFFF;
  border: 2px solid var(--border-color);
  border-radius: 12px;
  padding: 20px;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    border-color: var(--primary-color);
    box-shadow: 0 4px 20px rgba(212, 175, 55, 0.2);
    transform: translateY(-2px);
  }
`;

const OrderHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
`;

const OrderId = styled.p`
  color: var(--text-primary);
  font-weight: 600;
  margin: 0;
  font-size: 16px;
`;

const OrderStatus = styled.span`
  padding: 4px 12px;
  background: ${props => {
    switch (props.status) {
      case 'Completed':
        return 'rgba(76, 175, 80, 0.2)';
      case 'In Progress':
        return 'rgba(255, 193, 7, 0.2)';
      case 'Pending':
        return 'rgba(244, 67, 54, 0.2)';
      default:
        return 'rgba(100, 100, 100, 0.2)';
    }
  }};
  color: ${props => {
    switch (props.status) {
      case 'Completed':
        return '#4caf50';
      case 'In Progress':
        return '#ffc107';
      case 'Pending':
        return '#f44336';
      default:
        return '#999';
    }
  }};
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
`;

const OrderDetails = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  font-size: 13px;
  color: #999;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: #999;
`;

const Profile = () => {
  const { user, logout, userRole } = useAuth();
  const navigate = useNavigate();
  const [profileData, setProfileData] = useState(null);
  const [editData, setEditData] = useState({
    name: '',
    email: '',
    phone: '',
    address: ''
  });
  const [orders, setOrders] = useState([]);
  const [membership, setMembership] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [viewingOrderItems, setViewingOrderItems] = useState(null);
  const [orderItems, setOrderItems] = useState([]);

  useEffect(() => {
    // Clear previous user data when user changes
    setProfileData(null);
    setOrders([]);
    setEditData({ name: '', email: '', phone: '', address: '' });
    
    // If no user, don't try to fetch data
    if (!user) {
      setLoading(false);
      return;
    }

    setLoading(true);

    const fetchUserData = async () => {
      try {
        const response = await api.getProfile();
        if (response.success && response.data) {
          setProfileData(response.data);
          setEditData(response.data);
        }
      } catch (err) {
        // Silently handle 401 errors (user being redirected)
        if (err.status !== 401) {
          console.error('Error fetching user data:', err);
        }
      }
    };

    const fetchOrders = async () => {
      try {
        const response = await api.getOrders();
        if (response.success && response.data) {
          setOrders(response.data);
        }
      } catch (err) {
        // Silently handle 401 errors (user being redirected)
        if (err.status !== 401) {
          console.error('Error fetching orders:', err);
        }
      }
    };

    const fetchMembership = async () => {
      try {
        const response = await api.getMembership();
        if (response && response.data) {
          setMembership(response.data);
        }
      } catch (err) {
        // Silently handle errors - user may not have membership
        if (err.status !== 401 && err.status !== 404) {
          console.error('Error fetching membership:', err);
        }
      }
    };

    fetchUserData();
    fetchOrders();
    fetchMembership();
    setLoading(false);
  }, [user]);

  const handleSaveProfile = async () => {
    try {
      const response = await api.updateProfile({
        name: editData.name,
        phone: editData.phone,
        address: editData.address,
      });
      if (response.success) {
        setProfileData(editData);
        setIsEditing(false);
      }
    } catch (err) {
      console.error('Error updating profile:', err);
    }
  };

  const handleViewItems = async (orderId) => {
    try {
      const response = await api.getOrderItems(orderId);
      if (response.success) {
        setOrderItems(response.data || []);
        setViewingOrderItems(orderId);
      }
    } catch (err) {
      console.error('Error fetching order items:', err);
      alert('Failed to load order items');
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (err) {
      console.error('Error logging out:', err);
    }
  };

  if (loading || !profileData) {
    return <ProfileContainer>Loading...</ProfileContainer>;
  }

  return (
    <ProfileContainer>
      <ProfileContent>
        <Header>
          <Title>My Profile</Title>
          <div style={{ display: 'flex', gap: '10px' }}>
            {userRole === 'admin' && (
              <LogoutButton onClick={() => navigate('/admin/dashboard')} style={{ background: 'linear-gradient(135deg, #ff6b35, #ff8c42)' }}>
                Admin Dashboard
              </LogoutButton>
            )}
            <LogoutButton onClick={handleLogout}>
              <FiLogOut /> Logout
            </LogoutButton>
          </div>
        </Header>

        <GridContainer>
          <Card
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <CardTitle>
              Profile Information
              {!isEditing && (
                <EditButton onClick={() => setIsEditing(true)}>
                  <FiEdit2 />
                </EditButton>
              )}
            </CardTitle>

            {!isEditing ? (
              <>
                <InfoGroup>
                  <Label>Full Name</Label>
                  <Value>{profileData.name}</Value>
                </InfoGroup>
                <InfoGroup>
                  <Label>Email</Label>
                  <Value>{profileData.email}</Value>
                </InfoGroup>
                <InfoGroup>
                  <Label>Phone</Label>
                  <Value>{profileData.phone}</Value>
                </InfoGroup>
                <InfoGroup>
                  <Label>Address</Label>
                  <Value>{profileData.address}</Value>
                </InfoGroup>
              </>
            ) : (
              <>
                <InfoGroup>
                  <Label>Full Name</Label>
                  <Input
                    type="text"
                    value={editData.name}
                    onChange={(e) =>
                      setEditData({ ...editData, name: e.target.value })
                    }
                  />
                </InfoGroup>
                <InfoGroup>
                  <Label>Email</Label>
                  <Input type="email" value={editData.email} disabled />
                </InfoGroup>
                <InfoGroup>
                  <Label>Phone</Label>
                  <Input
                    type="tel"
                    value={editData.phone}
                    onChange={(e) =>
                      setEditData({ ...editData, phone: e.target.value })
                    }
                  />
                </InfoGroup>
                <InfoGroup>
                  <Label>Address</Label>
                  <Input
                    type="text"
                    value={editData.address}
                    onChange={(e) =>
                      setEditData({ ...editData, address: e.target.value })
                    }
                  />
                </InfoGroup>
                <ButtonGroup>
                  <SaveButton onClick={handleSaveProfile}>
                    <FiSave /> Save
                  </SaveButton>
                  <CancelButton onClick={() => setIsEditing(false)}>
                    <FiX /> Cancel
                  </CancelButton>
                </ButtonGroup>
              </>
            )}
          </Card>

          {membership && (
            <Card
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              style={{ gridColumn: '1 / -1' }}
            >
              <CardTitle>My Membership Plan</CardTitle>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px' }}>
                <InfoGroup>
                  <Label>Plan Name</Label>
                  <Value style={{ color: 'var(--primary-color)', fontWeight: '700', fontSize: '18px' }}>
                    {membership.plan_name}
                  </Value>
                </InfoGroup>
                <InfoGroup>
                  <Label>Total Clothes</Label>
                  <Value>{membership.clothes_limit + membership.bonus_clothes} clothes/month</Value>
                </InfoGroup>
                <InfoGroup>
                  <Label>Clothes Used</Label>
                  <Value style={{ color: '#f44336' }}>{membership.clothes_used}</Value>
                </InfoGroup>
                <InfoGroup>
                  <Label>Clothes Remaining</Label>
                  <Value style={{ color: '#4caf50', fontWeight: '700', fontSize: '20px' }}>
                    {membership.clothes_remaining}
                  </Value>
                </InfoGroup>
                <InfoGroup>
                  <Label>Start Date</Label>
                  <Value>{new Date(membership.start_date).toLocaleDateString()}</Value>
                </InfoGroup>
                <InfoGroup>
                  <Label>End Date</Label>
                  <Value>{new Date(membership.end_date).toLocaleDateString()}</Value>
                </InfoGroup>
              </div>
              <div style={{ marginTop: '20px', padding: '15px', background: 'rgba(212, 175, 55, 0.1)', borderRadius: '10px', border: '2px solid var(--primary-color)' }}>
                <p style={{ margin: 0, color: 'var(--text-secondary)', fontSize: '14px', lineHeight: '1.6' }}>
                  👕 <strong>Daily Wear Only:</strong> Membership applies only to Steam Iron items (Shirt, T-shirt, Trouser, Jeans, Pant, etc.)
                </p>
                <p style={{ margin: '8px 0 0 0', color: 'var(--text-secondary)', fontSize: '14px', lineHeight: '1.6' }}>
                  💡 <strong>Bonus Clothes:</strong> {membership.bonus_clothes} bonus clothes will be available after using all {membership.clothes_limit} regular clothes.
                </p>
              </div>
            </Card>
          )}

          <Card
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: membership ? 0.2 : 0.1 }}
            style={{ gridColumn: '1 / -1' }}
          >
            <CardTitle>
              Order History
              <AddButton onClick={() => navigate('/select-items')}>
                <FiEdit2 /> Place New Order
              </AddButton>
            </CardTitle>
            {orders.length > 0 ? (
              <OrdersList>
                {orders.map((order) => (
                  <OrderItem
                    key={order.id}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.3 }}
                    onClick={() => handleViewItems(order.id)}
                  >
                    <OrderHeader>
                      <OrderId>Order #{String(order.id).slice(0, 8)}</OrderId>
                      <OrderStatus status={order.status}>
                        {order.status}
                      </OrderStatus>
                    </OrderHeader>
                    <OrderDetails>
                      <span>
                        {order.order_number || `Order #${String(order.id).slice(0, 8)}`}
                      </span>
                      <span>
                        Pickup: {order.pickup_date ? new window.Date(order.pickup_date).toLocaleDateString() : 'N/A'}
                        {order.pickup_time_slot ? ` (${order.pickup_time_slot})` : ''}
                      </span>
                      <span>
                        Amount: ₹{order.final_amount || order.total_amount || 0}
                      </span>
                      <span>
                        {order.created_at ? (new window.Date(order.created_at)).toLocaleDateString() : (order.createdAt?.toDate?.().toLocaleDateString?.() || 'N/A')}
                      </span>
                    </OrderDetails>
                  </OrderItem>
                ))}
              </OrdersList>
            ) : (
              <EmptyState>No orders yet. Start by placing an order!</EmptyState>
            )}
          </Card>
        </GridContainer>

        {viewingOrderItems && (
          <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={() => setViewingOrderItems(null)}>
            <div style={{ background: '#fff', borderRadius: '12px', padding: '30px', maxWidth: '600px', width: '90%', maxHeight: '80vh', overflowY: 'auto' }} onClick={(e) => e.stopPropagation()}>
              <h2 style={{ color: '#333', marginBottom: '20px' }}>Order Items</h2>
              {orderItems.length > 0 ? (
                <div>
                  {orderItems.map((item, index) => (
                    <div key={index} style={{ padding: '12px', borderBottom: '1px solid #eee', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <div style={{ color: '#333', fontWeight: '600', marginBottom: '4px' }}>{item.item_name}</div>
                        <div style={{ color: '#666', fontSize: '14px' }}>Quantity: {item.quantity}</div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ color: '#d4af37', fontWeight: '600' }}>₹{item.price_per_item}</div>
                        <div style={{ color: '#666', fontSize: '14px' }}>Total: ₹{(item.price_per_item * item.quantity).toFixed(2)}</div>
                      </div>
                    </div>
                  ))}
                  <div style={{ padding: '15px', background: '#f5f5f5', marginTop: '10px', display: 'flex', justifyContent: 'space-between', fontWeight: 'bold', fontSize: '18px' }}>
                    <span style={{ color: '#333' }}>Order Total:</span>
                    <span style={{ color: '#d4af37' }}>₹{orderItems.reduce((sum, item) => sum + (item.price_per_item * item.quantity), 0).toFixed(2)}</span>
                  </div>
                </div>
              ) : (
                <div style={{ padding: '20px', textAlign: 'center', color: '#999' }}>No items found for this order</div>
              )}
              <button onClick={() => setViewingOrderItems(null)} style={{ marginTop: '20px', padding: '12px 24px', background: '#d4af37', border: 'none', borderRadius: '8px', color: '#fff', fontWeight: '600', cursor: 'pointer', width: '100%' }}>Close</button>
            </div>
          </div>
        )}
      </ProfileContent>
    </ProfileContainer>
  );
};

export default Profile;
